import type React from "react";

import type { MarginInterface } from "../utilities/interfaces";

export type OptionProp = {
  style?: React.CSSProperties;
  label?: string;
  value?: string;
  description?: string;
  disabled?: boolean;
  checkbox?: boolean;
  faIcon?: string;
  showAvatar?: boolean;
  radio?: boolean;
  imgSrc?: string;
  isSelected?: boolean;
  onClick?: () => void;
  options?: OptionProp[];
  customOptions?: React.ReactNode;
  children?: React.ReactNode;
  data?: {
    value?: string;
    disabled?: boolean;
    isSelected?: boolean;
    faIcon?: string;
    imgSrc?: string;
    label?: string;
    description?: string;
    onClick?: () => void;
    options?: OptionProp[];
  };
  innerProps?: {
    disabled?: boolean;
    onClick?: () => void;
  };
};

export type ValueProps = string | string[] | null;

export type SelectStyleProps = {
  helperTextStyle?: React.CSSProperties;
  optionStyle?: React.CSSProperties;
  controlStyle?: React.CSSProperties;
  valueContainerStyle?: React.CSSProperties;
  indicatorsContainerStyle?: React.CSSProperties;
  indicatorSeparatorStyle?: React.CSSProperties;
  inputStyle?: React.CSSProperties;
  placeholderStyle?: React.CSSProperties;
  singleValueStyle?: React.CSSProperties;
  labelStyle?: React.CSSProperties;
  requiredStyle?: React.CSSProperties;
};

export const getSelectStyleProps = <T extends SelectStyleProps>(props: T) => {
  const {
    helperTextStyle,
    optionStyle,
    controlStyle,
    valueContainerStyle,
    indicatorsContainerStyle,
    indicatorSeparatorStyle,
    inputStyle,
    placeholderStyle,
    singleValueStyle,
    labelStyle,
    requiredStyle,
  } = props;

  return {
    helperTextStyle,
    optionStyle,
    controlStyle,
    valueContainerStyle,
    indicatorsContainerStyle,
    indicatorSeparatorStyle,
    inputStyle,
    placeholderStyle,
    singleValueStyle,
    labelStyle,
    requiredStyle,
  };
};

export type SelectProps<TValue extends ValueProps> = MarginInterface &
  SelectStyleProps & {
    label?: string;
    required?: boolean;
    autoFocus?: boolean;
    disabled?: boolean;
    multiValues?: boolean;
    type?: "basic" | "popout" | "accordion" | "breadcrumbs";
    creatable?: boolean;
    emptyOption?: boolean;
    loading?: boolean;
    showAvatars?: boolean;
    checkbox?: boolean;
    radio?: boolean;
    noOptionsMessage?: string;
    helperText?: string;
    onBlur?: () => void;
    error?: string | null;
    style?: React.CSSProperties;
    value?: TValue;
    options?: OptionProp[];
    hideSearch?: boolean;
    selectedValuesAtTop?: boolean;
    showSelectAll?: boolean;
    autoFocusSearch?: boolean;
    useLatestSelect?: boolean; // This guarantees that the latest version of the select is used and not react-select
    onChange?: (value: TValue, parentPath?: string) => void;
    placeholder?: string;
    id?: string;
    testId?: string;
    customComponent?: React.ReactNode;
    menuClosed?: () => void;
    menuOpened?: () => void;
    closeMenu?: boolean; // When set to true a useEffect will close the menu
    // Deprecated props
    isDisabled?: boolean;
    isCreatable?: boolean;
    isLoading?: boolean;
    isMulti?: boolean;
  };
